import { motionQuery, prefersReducedMotion } from './reduced-motion';

const quoteSliderEls = document.querySelectorAll('.js-quoteSlider');
const trustedByEl = document.getElementById('js-trustedBySlider');
const fadingLogosEl = document.getElementById('js-fadingLogosSlider');

if (quoteSliderEls.length || trustedByEl || fadingLogosEl) {
    (async () => {
        const [{ default: Splide }, { AutoScroll }] = await Promise.all([
            import('@splidejs/splide'),
            import('@splidejs/splide-extension-auto-scroll'),
        ]);

        quoteSliderEls.forEach((slider) => {
            const quoteSlider = new Splide(slider, {
                role: 'region',
                type: 'loop',
                perPage: 1,
                perMove: 1,
                speed: prefersReducedMotion() ? 0 : 300,
                arrows: true,
                pagination: true,
                // Deliberately no `isNavigation`: it is for a carousel that drives a second,
                // synced one, and nothing here syncs. Setting it disabled Splide's own Live
                // component (`live && !options.isNavigation`), which is why the track carried
                // no aria-live/-atomic/-busy, and it put role="button" on every slide, which
                // the mounted handler below then had to strip back off (FLYR-669).
                // Mounted stopped rather than `autoplay: false` so Splide still binds its
                // Autoplay listeners (pause on hover and focus, and the toggle's own click
                // handler). With `autoplay: false` the component returns early from mount,
                // which left the play toggle inert and gave the preference no way back in.
                // The real start happens below, once the preference has been consulted.
                autoplay: 'pause',
                interval: 10000,
                keyboard: 'focused',
                i18n: {
                    carousel: '',
                    prev: '',
                    next: '',
                    first: '',
                    last: '',
                    play: '',
                    pause: '',
                },
            });

            // Fix accessibility issue by removing role="presentation" from list
            quoteSlider.on('mounted', () => {
                const list = slider.querySelector('.splide__list');
                if (list && list.getAttribute('role') === 'presentation') {
                    list.removeAttribute('role');
                }
            });

            quoteSlider.mount();

            const toggle = slider.querySelector('.splide__toggle');

            // Re-read the preference on every resume instead of trusting the value captured
            // at init. `autoplay:play` is the one funnel every resume path goes through (the
            // toggle's click, and Splide's own restart when the pointer or focus leaves), so
            // a single listener here holds for all of them rather than guarding one handler.
            const applyMotionPreference = () => {
                const reduced = prefersReducedMotion();
                if (reduced) {
                    quoteSlider.Components.Autoplay.pause();
                }
                // A Play button that refuses to play is its own defect, and there is nothing
                // to toggle while autoplay cannot run. Same treatment the fading-logos pause
                // control gets below.
                toggle?.classList.toggle('hidden', reduced);
            };

            quoteSlider.on('autoplay:play', applyMotionPreference);
            motionQuery.addEventListener('change', applyMotionPreference);
            applyMotionPreference();

            if (!prefersReducedMotion()) {
                quoteSlider.Components.Autoplay.play();
            }
        });

        if (trustedByEl) {
            let trustedBySlider;

            // Grab the prefers reduced media query.
            const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

            // Check if the media query matches or is not available.
            if (!mediaQuery || mediaQuery.matches) {
                // Slider with manual controls
                trustedBySlider = new Splide('#js-trustedBySlider', {
                    role: 'group',
                    type: 'loop',
                    focus: 'center',
                    autoWidth: true,
                    gap: 80,
                    arrows: true,
                    pagination: false,
                });
                trustedBySlider.mount();
            } else {
                // Slider with auto scroll
                trustedBySlider = new Splide('#js-trustedBySlider', {
                    role: 'group',
                    type: 'loop',
                    drag: 'free',
                    focus: 'center',
                    pauseOnHover: false,
                    autoWidth: true,
                    gap: 32,
                    arrows: false,
                    pagination: false,
                    autoScroll: {
                        speed: 1,
                    },
                });

                trustedBySlider.mount({ AutoScroll });
            }

            let paused = false;
            $('#trustedBySliderPause').on('click', () => {
                const $pauseTxt = $('#trustedBySliderPauseText');
                const $resumeTxt = $('#trustedBySliderResumeText');
                const $pauseIcon = $('#trustedBySliderPauseIcon');
                const $playIcon = $('#trustedBySliderPlayIcon');
                if (!paused) {
                    trustedBySlider.Components.AutoScroll.pause();
                    paused = true;
                    $resumeTxt.removeClass('hidden');
                    $pauseTxt.addClass('hidden');
                    $playIcon.removeClass('hidden');
                    $pauseIcon.addClass('hidden');
                } else {
                    trustedBySlider.Components.AutoScroll.play();
                    paused = false;
                    $pauseTxt.removeClass('hidden');
                    $resumeTxt.addClass('hidden');
                    $pauseIcon.removeClass('hidden');
                    $playIcon.addClass('hidden');
                }
            });
        }

        if (fadingLogosEl) {
            const { Grid } = await import('@splidejs/splide-extension-grid');

            const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

            // Per-logo cascade timing.
            const STEP = 270; // stagger between logos
            const FADE = 1050; // each logo's fade duration
            const HOLD = 5000; // how long a fully-shown page rests before fading out

            const colsOf = (page) => [...page.querySelectorAll('.splide__slide--col')];

            // Cascade duration for a page, scaled to its real column count so a
            // partial last page (e.g. 11 logos → 5 cols) fades faster than a full one.
            const spanOf = (page) => FADE + STEP * Math.max(0, colsOf(page).length - 1);

            // Pages are derived by the grid extension: ceil(logos / cols-per-page).
            const pageCount = () => fadingLogosEl.querySelectorAll('.splide__list > .splide__slide').length;

            // Stagger every logo in a page in (target=1) or out (target=0),
            // fading opacity and easing a slight scale alongside it.
            const cascade = (page, target) => {
                const fadingIn = target === 1;
                colsOf(page).forEach((col, i) => {
                    col.style.transition = `opacity ${FADE}ms ease, transform ${FADE}ms ease`;
                    col.style.transitionDelay = `${i * STEP}ms`;
                    col.style.opacity = String(target);
                    col.style.transform = fadingIn ? 'scale(1)' : 'scale(0.98)';
                });
            };

            // Snap a page's logos to fully visible, no animation.
            const snapVisible = (page) => {
                if (!page) {
                    return;
                }
                colsOf(page).forEach((col) => {
                    col.style.transition = 'none';
                    col.style.opacity = '1';
                    col.style.transform = 'scale(1)';
                });
            };

            // Rotation is gated on the section entering the viewport.
            let inView = false;

            const fadingLogosSlider = new Splide(fadingLogosEl, {
                role: 'group',
                type: 'fade',
                rewind: true,
                speed: 400,
                arrows: false,
                pagination: false,
                drag: false,
                // Cycling is driven manually below so each page fades OUT before
                // the next fades IN (rather than Splide's simultaneous crossfade).
                autoplay: false,
                grid: { rows: 1, cols: 6, gap: { row: '1.5rem', col: '2rem' } },
                breakpoints: {
                    2399: { grid: { rows: 1, cols: 4 } },
                    767: { grid: { rows: 1, cols: 2 } },
                },
            });

            fadingLogosSlider.on('mounted', () => {
                const list = fadingLogosEl.querySelector('.splide__list');
                if (list && list.getAttribute('role') === 'presentation') {
                    list.removeAttribute('role');
                }
            });

            // When a page becomes active, cascade its logos in — but only once the
            // section is in view (see IntersectionObserver below). Before that, leave
            // the columns at their natural opacity so the logos are visible at first
            // paint instead of being hidden by a reveal that plays below the fold.
            fadingLogosSlider.on('active', (Slide) => {
                if (!inView) {
                    return;
                }
                if (mediaQuery.matches) {
                    snapVisible(Slide.slide);
                    return;
                }
                colsOf(Slide.slide).forEach((col) => {
                    col.style.transition = 'none';
                    col.style.opacity = '0';
                    col.style.transform = 'scale(0.98)';
                });
                void Slide.slide.offsetWidth;
                cascade(Slide.slide, 1);
            });

            fadingLogosSlider.mount({ Grid });

            // Manual loop: hold → cascade current page OUT → advance (cascades next IN).
            let timer = null;
            let paused = mediaQuery.matches;

            const stop = () => {
                if (timer) {
                    clearTimeout(timer);
                    timer = null;
                }
            };

            const $pauseBtn = $('#fadingLogosSliderPause');

            // Show the pause control only when there's motion to pause (multiple
            // pages, motion allowed). With a single page, force its logos visible
            // since the cycle that would fade them in never runs.
            const syncControls = () => {
                const multi = pageCount() > 1;
                $pauseBtn.toggleClass('hidden', !(multi && !mediaQuery.matches));
                if (!multi) {
                    snapVisible(fadingLogosEl.querySelector('.splide__slide.is-active'));
                }
            };

            const loop = () => {
                // Re-derive page count each tick: a resize may have collapsed the
                // logos onto one page, where cascading OUT would strand them hidden.
                if (pageCount() <= 1) {
                    stop();
                    return;
                }
                const active = fadingLogosEl.querySelector('.splide__slide.is-active');
                const span = active ? spanOf(active) : FADE;
                if (active) cascade(active, 0);
                timer = setTimeout(() => {
                    fadingLogosSlider.go('>');
                    timer = setTimeout(loop, span + HOLD);
                }, span);
            };

            const start = () => {
                stop();
                if (inView && !paused && !mediaQuery.matches && pageCount() > 1) {
                    timer = setTimeout(loop, HOLD);
                }
            };

            // Re-grid on resize changes cols-per-page (and thus the page count),
            // so re-evaluate the control and restart the cycle to fit the new layout.
            fadingLogosSlider.on('resize', () => {
                syncControls();
                start();
            });

            syncControls();

            // Drive rotation off viewport visibility: the strip sits below the fold on
            // load, so a mount-time cycle would advance/fade pages unseen and leave a user
            // who scrolls in landing on a faded/blank page (FLYR-610). The first page is
            // shown as-is (no fade-in); subsequent pages animate as the cycle rotates.
            // Pause the cycle while the section is out of view.
            const io = new IntersectionObserver(
                (entries) => {
                    inView = entries[0].isIntersecting;
                    if (!inView) {
                        stop();
                        return;
                    }
                    snapVisible(fadingLogosEl.querySelector('.splide__slide.is-active'));
                    start();
                },
                { threshold: 0.5 }
            );
            io.observe(fadingLogosEl);

            $('#fadingLogosSliderPause').on('click', () => {
                const $pauseTxt = $('#fadingLogosSliderPauseText');
                const $resumeTxt = $('#fadingLogosSliderResumeText');
                const $pauseIcon = $('#fadingLogosSliderPauseIcon');
                const $playIcon = $('#fadingLogosSliderPlayIcon');
                if (!paused) {
                    paused = true;
                    stop();
                    $resumeTxt.removeClass('hidden');
                    $pauseTxt.addClass('hidden');
                    $playIcon.removeClass('hidden');
                    $pauseIcon.addClass('hidden');
                } else {
                    paused = false;
                    start();
                    $pauseTxt.removeClass('hidden');
                    $resumeTxt.addClass('hidden');
                    $pauseIcon.removeClass('hidden');
                    $playIcon.addClass('hidden');
                }
            });
        }
    })();
}
